import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import {
  MessageSquare, ArrowRight, CheckCircle2, ChevronRight,
  X, Building2, ArrowLeft, Calculator,
} from "lucide-react";

import type { Easing } from "framer-motion";

const ease: Easing = [0.25, 0.1, 0.25, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease } }),
};

const ALL_FEATURES = [
  "Mensagens/mês",
  "Chamadas IA/mês",
  "Grupos",
  "Membros",
  "Skills & Comandos",
  "Agentes IA",
  "Memory Engine",
  "Intelligence Layer",
  "Suporte AI-First",
  "SLA",
  "Suporte",
];

interface PlanDetail {
  name: string;
  priceMonthly: string;
  priceAnnual: string;
  savingsAnnual?: string;
  highlight: boolean;
  badge?: string;
  features: { label: string; included: boolean }[];
}

const PLANS: PlanDetail[] = [
  {
    name: "Free",
    priceMonthly: "R$0",
    priceAnnual: "R$0",
    highlight: false,
    features: [
      { label: "500", included: true },
      { label: "50", included: true },
      { label: "2", included: true },
      { label: "50", included: true },
      { label: "Básicos", included: true },
      { label: "1", included: true },
      { label: "", included: false },
      { label: "", included: false },
      { label: "", included: false },
      { label: "-", included: false },
      { label: "Comunidade", included: true },
    ],
  },
  {
    name: "Starter",
    priceMonthly: "R$197",
    priceAnnual: "R$167",
    savingsAnnual: "Economize R$360/ano",
    highlight: false,
    features: [
      { label: "5.000", included: true },
      { label: "500", included: true },
      { label: "10", included: true },
      { label: "200", included: true },
      { label: "✓", included: true },
      { label: "2", included: true },
      { label: "Recente", included: true },
      { label: "", included: false },
      { label: "", included: false },
      { label: "-", included: false },
      { label: "E-mail", included: true },
    ],
  },
  {
    name: "Pro",
    priceMonthly: "R$497",
    priceAnnual: "R$417",
    savingsAnnual: "Economize R$960/ano",
    highlight: true,
    badge: "Mais popular",
    features: [
      { label: "25.000", included: true },
      { label: "2.500", included: true },
      { label: "50", included: true },
      { label: "1.000", included: true },
      { label: "✓", included: true },
      { label: "5", included: true },
      { label: "5 camadas", included: true },
      { label: "✓", included: true },
      { label: "✓", included: true },
      { label: "24h", included: true },
      { label: "Prioritário", included: true },
    ],
  },
  {
    name: "Business",
    priceMonthly: "R$997",
    priceAnnual: "R$837",
    savingsAnnual: "Economize R$1.920/ano",
    highlight: false,
    features: [
      { label: "100.000", included: true },
      { label: "10.000", included: true },
      { label: "200", included: true },
      { label: "5.000", included: true },
      { label: "✓", included: true },
      { label: "Ilimitado", included: true },
      { label: "5 camadas", included: true },
      { label: "✓", included: true },
      { label: "✓", included: true },
      { label: "4h", included: true },
      { label: "Dedicado", included: true },
    ],
  },
];

/* ─── ROI Calculator ─── */
function ROICalculator() {
  const [communitySize, setCommunitySize] = useState(500);
  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [hourlyRate, setHourlyRate] = useState(80);

  const monthlyCostWithout = hoursPerWeek * 4.33 * hourlyRate;
  const estimatedReduction = 0.65; // 65% time savings
  const monthlySavings = monthlyCostWithout * estimatedReduction;
  const suggestedPlan = communitySize <= 50 ? 0 : communitySize <= 200 ? 197 : communitySize <= 1000 ? 497 : 997;
  const netSavings = monthlySavings - suggestedPlan;
  const annualSavings = netSavings * 12;
  const paybackMonths = netSavings > 0 ? Math.max(1, Math.ceil(suggestedPlan / netSavings)) : 0;

  return (
    <Card className="border-primary/20 max-w-2xl mx-auto">
      <CardContent className="p-8 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="h-6 w-6 text-primary" />
          <h3 className="text-xl font-bold font-headline">Calculadora de ROI</h3>
        </div>
        <p className="text-sm text-muted-foreground">Descubra quanto SYRA economiza na sua operação.</p>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="size" className="text-xs font-medium">Tamanho da comunidade</Label>
            <Input
              id="size"
              type="number"
              min={10}
              max={50000}
              value={communitySize}
              onChange={(e) => setCommunitySize(Math.max(10, Math.min(50000, Number(e.target.value) || 10)))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hours" className="text-xs font-medium">Horas/semana em suporte</Label>
            <Input
              id="hours"
              type="number"
              min={1}
              max={80}
              value={hoursPerWeek}
              onChange={(e) => setHoursPerWeek(Math.max(1, Math.min(80, Number(e.target.value) || 1)))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate" className="text-xs font-medium">Custo/hora (R$)</Label>
            <Input
              id="rate"
              type="number"
              min={10}
              max={1000}
              value={hourlyRate}
              onChange={(e) => setHourlyRate(Math.max(10, Math.min(1000, Number(e.target.value) || 10)))}
            />
          </div>
        </div>

        {netSavings > 0 ? (
          <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t border-border/50">
            <div className="text-center p-4 rounded-xl bg-primary/5">
              <p className="text-2xl font-extrabold font-headline text-primary">
                {paybackMonths === 1 ? "<1" : paybackMonths}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{paybackMonths === 1 ? "mês" : "meses"} de payback</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-primary/5">
              <p className="text-2xl font-extrabold font-headline text-primary">
                R${Math.round(annualSavings).toLocaleString("pt-BR")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">economia anual</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-primary/5">
              <p className="text-2xl font-extrabold font-headline text-primary">
                R${suggestedPlan}
              </p>
              <p className="text-xs text-muted-foreground mt-1">plano sugerido/mês</p>
            </div>
          </div>
        ) : (
          <div className="text-center p-4 rounded-xl bg-muted/50 text-sm text-muted-foreground">
            O plano Free já atende sua operação atual. Escale quando crescer.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-50 glass-card border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <MessageSquare className="w-7 h-7 text-primary" />
            <span className="text-xl font-bold font-headline tracking-tight text-primary">SYRA</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
            </Button>
            <Button size="sm" onClick={() => navigate("/login")} className="gradient-primary text-primary-foreground">
              Começar grátis
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div className="text-center mb-10" initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <h1 className="text-4xl sm:text-5xl font-extrabold font-headline mb-4">Compare os planos</h1>
            <p className="text-muted-foreground text-lg mb-6">Encontre o plano ideal para o tamanho da sua comunidade</p>
            <div className="inline-flex items-center gap-3 bg-muted/50 rounded-full p-1">
              <button
                onClick={() => setAnnual(false)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!annual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Mensal
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${annual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Anual <span className="text-xs ml-1 opacity-80">(-15%)</span>
              </button>
            </div>
          </motion.div>

          {/* Comparison table */}
          <motion.div className="overflow-x-auto mb-16" initial="hidden" animate="visible" variants={fadeUp} custom={1}>
            <table className="w-full text-sm border-collapse min-w-[700px]">
              <thead>
                <tr>
                  <th className="text-left p-3 font-semibold text-muted-foreground w-44">Recurso</th>
                  {PLANS.map((plan) => (
                    <th key={plan.name} className={`p-3 text-center ${plan.highlight ? "bg-primary/5 rounded-t-xl" : ""}`}>
                      <div className="space-y-1">
                        {plan.badge && <Badge className="gradient-primary text-primary-foreground text-xs">{plan.badge}</Badge>}
                        <p className="font-bold font-headline text-base">{plan.name}</p>
                        <p className="text-xl font-extrabold font-headline">{annual ? plan.priceAnnual : plan.priceMonthly}</p>
                        {annual && plan.savingsAnnual && <p className="text-xs text-primary font-medium">{plan.savingsAnnual}</p>}
                        <p className="text-xs text-muted-foreground">/mês</p>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ALL_FEATURES.map((feature, fi) => (
                  <tr key={feature} className="border-t border-border/50">
                    <td className="p-3 font-medium text-muted-foreground">{feature}</td>
                    {PLANS.map((plan) => {
                      const f = plan.features[fi];
                      return (
                        <td key={plan.name} className={`p-3 text-center ${plan.highlight ? "bg-primary/5" : ""}`}>
                          {f.included ? (
                            f.label === "✓" ? (
                              <CheckCircle2 className="h-4 w-4 text-primary mx-auto" />
                            ) : (
                              <span className="font-medium">{f.label}</span>
                            )
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* CTA row */}
                <tr className="border-t border-border/50">
                  <td className="p-3"></td>
                  {PLANS.map((plan) => (
                    <td key={plan.name} className={`p-3 text-center ${plan.highlight ? "bg-primary/5 rounded-b-xl" : ""}`}>
                      <Button
                        size="sm"
                        variant={plan.highlight ? "default" : "outline"}
                        className={plan.highlight ? "gradient-primary text-primary-foreground" : ""}
                        onClick={() => navigate("/login")}
                      >
                        {plan.priceMonthly === "R$0" ? "Grátis" : "Trial grátis"}
                      </Button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </motion.div>

          {/* ROI Calculator */}
          <motion.div className="mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <ROICalculator />
          </motion.div>

          {/* Enterprise CTA */}
          <motion.div className="text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Card className="max-w-2xl mx-auto border-primary/20">
              <CardContent className="p-8 text-center space-y-4">
                <Building2 className="h-10 w-10 text-primary mx-auto" />
                <h3 className="text-2xl font-bold font-headline">Precisa de mais?</h3>
                <p className="text-muted-foreground">Para operações com mais de 200 grupos, SLA dedicado e onboarding assistido.</p>
                <Button onClick={() => navigate("/contact")} className="gradient-primary text-primary-foreground">
                  Agendar demo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
